import { useMemo } from "react";
import { useAuthenticatedUser } from "@/hooks/useAuthenticatedUser";
import {
  AccessLimits,
  AccessFeatureMap,
  getAccessLimits,
  buildCapabilities,
  CapabilityMap,
  getPermissionSet,
  hasCorePlatformAccess,
  PermissionSet,
  toFeatureMap,
} from "@/utils/accessControl";

interface UseAccessCapabilitiesReturn {
  capabilities: CapabilityMap;
  featureMap: AccessFeatureMap;
  limits: AccessLimits;
  permissionSet: PermissionSet;
  hasActiveSubscription: boolean;
  hasFeedbacksAccess: boolean;
  hasReservationsAccess: boolean;
}

export const useAccessCapabilities = (): UseAccessCapabilitiesReturn => {
  const { userProfile } = useAuthenticatedUser();

  const featureMap = useMemo(() => toFeatureMap(userProfile), [userProfile]);
  const capabilities = useMemo(
    () => buildCapabilities(userProfile),
    [userProfile],
  );
  const limits = useMemo(() => getAccessLimits(userProfile), [userProfile]);
  const permissionSet = useMemo(
    () => getPermissionSet(userProfile),
    [userProfile],
  );

  return {
    capabilities,
    featureMap,
    limits,
    permissionSet,
    hasActiveSubscription: userProfile?.access?.hasActiveSubscription ?? false,
    hasFeedbacksAccess: capabilities.canUseFeedbacks,
    hasReservationsAccess: hasCorePlatformAccess(userProfile, "reservations"),
  };
};
